import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  Divider,
  CircularProgress,
  styled
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell
} from 'recharts';
import { useTranslation } from 'react-i18next';

const CHART_COLORS = ['#FF9800', '#1976D2', '#4CAF50', '#9C27B0', '#E91E63', '#00BCD4', '#F44336', '#795548'];

const ChartContainer = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
  borderRadius: 8,
  '& .MuiTypography-h6': {
    fontSize: '1rem',
    fontWeight: 'bold',
  },
}));

interface BankAccount {
  id: string;
  bankName: string;
  branchName: string;
  accountNumber: string;
  iban: string;
  currency: string;
  balance: number;
  isActive: boolean;
  branchId?: string | null;
}

interface BankBalanceChartProps {
  data: BankAccount[];
  loading: boolean;
  getCurrencySymbol: (currency: string) => string;
}

const BankBalanceChart: React.FC<BankBalanceChartProps> = ({ data, loading, getCurrencySymbol }) => {
  const { t, i18n } = useTranslation('accounting');
  const locale = i18n.resolvedLanguage?.startsWith('en') ? 'en-US' : 'tr-TR';

  const chartData = useMemo(() => {
    return data
      .filter(b => b.isActive)
      .map(bank => ({
        name: bank.bankName.length > 12 ? `${bank.bankName.substring(0, 12)}...` : bank.bankName,
        fullName: bank.bankName,
        value: bank.balance,
        currency: bank.currency,
      }));
  }, [data]);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload;
      const formatted = new Intl.NumberFormat(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(item.value);
      return (
        <Box sx={{ bgcolor: 'background.paper', p: 2, boxShadow: 3, borderRadius: 1 }}>
          <Typography variant="body2" fontWeight="bold">{item.fullName}</Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
            <Box sx={{ width: 10, height: 10, bgcolor: payload[0].color, borderRadius: '50%' }} />
            <Typography variant="body2">{getCurrencySymbol(item.currency)}{formatted}</Typography>
          </Box>
        </Box>
      );
    }
    return null;
  };

  const formatYAxis = (value: number) => {
    if (Math.abs(value) >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (Math.abs(value) >= 1000) return `${(value / 1000).toFixed(0)}K`;
    return value.toString();
  };

  return (
    <ChartContainer>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6" fontWeight="bold">
          {t('dashboard.bankBalancesChartTitle')}
        </Typography>
      </Box>
      <Divider sx={{ mb: 1 }} />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1, minHeight: 180 }}>
          <CircularProgress />
        </Box>
      ) : chartData.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1, minHeight: 180 }}>
          <Typography variant="body2" color="textSecondary">{t('dashboard.bankChartNoData')}</Typography>
        </Box>
      ) : (
        // Esnek yükseklik: kart büyütülünce grafik de büyür
        <Box sx={{ flexGrow: 1, minHeight: 180 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 10, right: 10, left: 0, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 11 }}
              angle={-30}
              textAnchor="end"
              height={50}
            />
            <YAxis
              tickFormatter={formatYAxis}
              tick={{ fontSize: 11 }}
              width={45}
            />
            <RechartsTooltip content={<CustomTooltip />} />
            <Bar dataKey="value" name={t('dashboard.chartBalanceLabel')} barSize={30} radius={[4, 4, 0, 0]}>
              {chartData.map((_entry, index) => (
                <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        </Box>
      )}
    </ChartContainer>
  );
};

export default BankBalanceChart;
